import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
} from '@mui/material';

// Assuming MasterVideoInfo is defined elsewhere or we define it here
interface MasterVideoInfo {
  song: string;
  artist: string;
  s3Url: string;
  seekIn: string;
  position?: number;
}

// Add interfaces for settings
interface IntroSettings {
  carousel_video1_url?: string;
  carousel_video2_url?: string;
  carousel_video3_url?: string;
  carousel_video4_url?: string;
  video_left_url?: string;
  [key: string]: string | undefined;
}

interface OutroSettings {
  outro_video1_url?: string;
  outro_video2_url?: string;
  outro_video3_url?: string;
  outro_video4_url?: string;
  outro_video5_url?: string;
  outro_video6_url?: string;
  [key: string]: string | undefined;
}

interface VideoSelectionDialogProps {
  open: boolean;
  onClose: () => void;
  masterVideoList: MasterVideoInfo[];
  currentSlotId: string | null;
  onSave: (slotId: string, newS3Url: string, seekIn: string) => void;
  // Add new props
  activeTab: number; // 0 for Intro, 1 for Outro
  introSettings: IntroSettings;
  outroSettings: OutroSettings;
  // Add source prop
  source?: 'charts' | string;
}

export const VideoSelectionDialog: React.FC<VideoSelectionDialogProps> = ({ 
  open, 
  onClose, 
  masterVideoList, 
  currentSlotId, 
  onSave,
  activeTab = 0, // Default to Intro tab
  introSettings = {}, 
  outroSettings = {},
  source // New source prop to track origin
}) => {
  const navigate = useNavigate(); // React Router navigation hook
  const [searchTerm, setSearchTerm] = useState('');
  // Restore state to track the *initial* URL of the slot when the dialog opens
  const [initialUrl, setInitialUrl] = useState<string | null>(null);

  // Helper function to extract filename from URL
  const extractFilename = (url: string): string => {
    return url.split('/').pop()?.split('?')[0]?.toLowerCase() || '';
  };

  // Helper function to find a video in the master list using multiple matching strategies
  const findVideoInMasterList = (url: string): MasterVideoInfo | undefined => {
    if (!url) return undefined;
    
    const normalizedUrl = url.trim();
    // console.log(`[VideoSelect] Finding video in master list for URL: ${normalizedUrl}`);
    
    // Strategy 1: Exact match
    let video = masterVideoList.find(v => v.s3Url.trim() === normalizedUrl);
    if (video) {
      // console.log(`[VideoSelect] Found exact URL match for ${url}`);
      return video;
    }
    
    // Strategy 2: Case-insensitive match
    video = masterVideoList.find(v => 
      v.s3Url.toLowerCase().trim() === normalizedUrl.toLowerCase()
    );
    if (video) {
      // console.log(`[VideoSelect] Found case-insensitive URL match for ${url}`);
      return video;
    }
    
    // Strategy 3: Filename match
    const urlFilename = extractFilename(normalizedUrl);
    if (urlFilename) {
      // console.log(`[VideoSelect] Trying to match by filename: ${urlFilename}`);
      
      video = masterVideoList.find(v => {
        const masterFilename = extractFilename(v.s3Url);
        return masterFilename === urlFilename && masterFilename !== '';
      });
      
      if (video) {
        // console.log(`[VideoSelect] Found filename match: ${video.song} with seek time ${video.seekIn}`);
        return video;
      }
    }
    
    // console.log(`[VideoSelect] No matching video found for ${url} in master list with ${masterVideoList.length} videos`);
    // Log the first few entries in the master list for debugging
    /*
    if (masterVideoList.length > 0) {
      console.log(`[VideoSelect] Sample master list entries:`, 
        masterVideoList.slice(0, 3).map(v => ({ 
          url: v.s3Url, 
          filename: extractFilename(v.s3Url),
          seekIn: v.seekIn 
        }))
      );
    }
    */
    return undefined;
  };

  // Get the current URL for the selected slot when dialog opens
  useEffect(() => {
    if (open && currentSlotId) {
      // Get the current URL based on active tab and current slot ID
      const settings = activeTab === 0 ? introSettings : outroSettings;
      const currentUrl = settings[`${currentSlotId}_url`];
      
      console.log(`[VideoSelect] Dialog opened for slot ${currentSlotId}, initial URL: ${currentUrl || 'none'}`);
      
      // Set the initial URL state for highlighting
      setInitialUrl(currentUrl || null);
      
      // Reset search term when opening
      setSearchTerm('');
    } else if (!open) {
      // Reset initial URL when dialog closes
      setInitialUrl(null);
    }
  }, [open, currentSlotId, activeTab, introSettings, outroSettings]); // Removed masterVideoList and findVideoInMasterList from deps as they don't directly influence the *initial* URL setting

  // Determine which videos are already placed in the current tab only
  const placedVideosInCurrentTab = useMemo(() => {
    // Only get URLs from the current tab's settings
    const settings = activeTab === 0 ? introSettings : outroSettings;
    
    return Object.entries(settings)
      .filter(([key, value]) => key.endsWith('_url') && !!value)
      .map(([_, value]) => value as string);
  }, [activeTab, introSettings, outroSettings]);

  // Check if a video is already placed in the current tab
  const isVideoPlacedInCurrentTab = (videoUrl: string) => {
    // Ensure the comparison handles potential undefined/null values
    if (!videoUrl) return false; 
    return placedVideosInCurrentTab.some(url => url === videoUrl);
  };
  
  // Check if a video IS the one originally in the slot being edited
  const isCurrentSelection = (videoUrl: string) => {
    return initialUrl === videoUrl;
  };

  const filteredList = useMemo(() => {
    if (!searchTerm) {
      return masterVideoList;
    }
    const lowerSearchTerm = searchTerm.toLowerCase();
    return masterVideoList.filter(video => 
      video.song.toLowerCase().includes(lowerSearchTerm) ||
      video.artist.toLowerCase().includes(lowerSearchTerm) ||
      video.s3Url.toLowerCase().includes(lowerSearchTerm)
    );
  }, [masterVideoList, searchTerm]);

  // Enhanced close handler to navigate based on source
  const handleClose = () => {
    onClose();
    // If dialog was opened from charts page, navigate back there
    if (source === 'charts') {
      navigate('/');
    }
  };

  // Update handle video select to navigate when from charts
  const handleVideoSelect = (video: MasterVideoInfo) => {
    // Always log the entire video object for debugging
    console.log(`[VideoSelect] CLICKED VIDEO:`, video);
    console.log(`[VideoSelect] Selected video URL: ${video.s3Url}`);
    console.log(`[VideoSelect] Selected video seek: ${video.seekIn}`);
    
    // Directly call onSave and onClose when a video is clicked
    if (currentSlotId) {
      const seekValueToSave = video.seekIn || '0';
      console.log(`[VideoSelect] Immediately saving selection: Slot=${currentSlotId}, URL=${video.s3Url}, Seek=${seekValueToSave}`);
      onSave(currentSlotId, video.s3Url, seekValueToSave);
      
      // Check if we should navigate back to charts
      if (source === 'charts') {
        navigate('/');
      } else {
        onClose(); // Just close the dialog if not from charts
      }
    } else {
      console.error('[VideoSelect] Cannot save, currentSlotId is null');
      handleClose(); // Use enhanced close handler
    }
  };

  // Auto-scroll to the selected item after render
  useEffect(() => {
    // Scroll to the *initial* item when the dialog opens
    if (open && initialUrl) {
      // Use a small timeout to ensure the list has rendered
      const timer = setTimeout(() => {
        const selectedItem = document.getElementById(`video-item-${extractFilename(initialUrl)}`);
        if (selectedItem) {
          console.log(`[VideoSelect] Scrolling to initial item: ${initialUrl}`);
          selectedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } else {
          console.log(`[VideoSelect] Could not find initial item to scroll to: ${initialUrl}`);
        }
      }, 100); // 100ms delay
      return () => clearTimeout(timer);
    }
  }, [open, initialUrl]); // Depend on open and initialUrl

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Select Video</DialogTitle>
      <DialogContent>
        <TextField
          label="Search by title, artist or URL"
          variant="outlined"
          fullWidth
          margin="normal"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <List sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {filteredList.map((video) => {
            // Check if the video URL is not empty before processing
            if (!video.s3Url) return null;

            const isPlacedElsewhere = isVideoPlacedInCurrentTab(video.s3Url);
            const isCurrentlySelectedSlot = isCurrentSelection(video.s3Url);
            
            return (
              <ListItem 
                disablePadding 
                key={video.s3Url} // Use URL as key assuming it's unique
                id={`video-item-${extractFilename(video.s3Url)}`}
                sx={{
                  // Remove disabled styling
                  // opacity: isDisabled ? 0.5 : 1,
                  // cursor: isDisabled ? 'not-allowed' : 'pointer',
                  // Apply subtle background highlight for the item initially in the slot
                  bgcolor: isCurrentlySelectedSlot ? 'action.hover' : 'transparent',
                }}
              >
                <ListItemButton
                  // Remove the isDisabled check from onClick
                  onClick={() => handleVideoSelect(video)}
                  // The 'selected' prop isn't needed for persistent highlight anymore
                  // selected={initialUrl === video.s3Url} 
                  // Remove the disabled prop
                  // disabled={isDisabled}
                >
                  <ListItemText
                    // Apply conditional styling directly using Typography component
                    primary={
                      <Typography 
                        component="span" // Use span to avoid block display issues
                        variant="body1" // Match default ListItemText primary variant
                        sx={{
                          color: isPlacedElsewhere && !isCurrentlySelectedSlot ? 'primary.main' : 'inherit',
                          fontWeight: isCurrentlySelectedSlot ? 'bold' : 'normal'
                        }}
                      >
                        {video.song}
                      </Typography>
                    }
                    secondary={video.artist}
                    // Remove primaryTypographyProps as we style the primary text directly now
                    // primaryTypographyProps={{ 
                    //   style: {
                    //     color: isPlacedElsewhere ? 'primary.main' : 'inherit',
                    //     fontWeight: isCurrentlySelectedSlot ? 'bold' : 'normal'
                    //   }
                    // }}
                  />
                  {/* Keep labels for clarity */}
                  {isCurrentlySelectedSlot && <Typography variant="caption" sx={{ ml: 1 }}>(Current)</Typography>}
                  {isPlacedElsewhere && !isCurrentlySelectedSlot && <Typography variant="caption" sx={{ ml: 1 }}>(Placed)</Typography>}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}; 